import { P2GCheckBoxLegacy, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { GetExternalTrackingNumber } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { ProductImage } from '../../product/ProductImage';
import { GetPickingListMarketPlaceOrderIds } from '../../smartsend/item/OrderReferenceNumberUtils';
import { ItemActionType } from '../../smartsend/item/SetMarketplaceAction';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { MarketplaceSupportsMarkingAsDispatched } from '~/apps/smartsend/lib/helpers/MarketplaceSupportsMarkingAsDispatched';
import { useMemo } from 'react';

interface props {
  item: SmartSendItemApiModel;
  action: (action: ItemActionType, id: string) => void;
  packaged: boolean;
  dispatched: boolean;
  working: boolean;
}

export function PickingListSheetRow(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const p = { padding: '10px' };
  const sourceSupportsMarkingAsDispatched = useMemo(
    () => MarketplaceSupportsMarkingAsDispatched(props.item),
    [props.item]
  );

  return (
    <Fragment>
      <tr>
        <td style={{ ...p, backgroundColor: '#f1f1f1', width: '220px' }}>
          <div>
            <P2GText variant="h3">{props.item.BookingDetails?.Reference}</P2GText>
          </div>
          <P2GText>{GetExternalTrackingNumber(props.item)}</P2GText>
        </td>
        <td style={{ ...p, backgroundColor: '#e1e1e1' }}>
          <P2GText component="p" variant="body2">
            <strong>
              {props.item.Recipient.Name}, {props.item.Recipient.AddressLine1} {props.item.Recipient.AddressLine2},
              {props.item.Recipient.Postcode}, {props.item.Recipient.Email}
            </strong>
          </P2GText>
        </td>
        <td style={{ ...p, backgroundColor: '#d1d1d1' }}>
          <P2GText component="p" variant="body2">
            <strong>{props.item.BookingDetails?.Service?.Name}</strong>
          </P2GText>
          {props.item.PackageDimensions.Length > 0 ? (
            <P2GText component="p" variant="body2">
              {props.item.PackageDimensions.Weight}kg | {props.item.PackageDimensions.Length}x
              {props.item.PackageDimensions.Width}x{props.item.PackageDimensions.Height}cm
            </P2GText>
          ) : (
            <P2GText component="p" variant="body2">
              {props.item.PackageDimensions.Weight}kg
            </P2GText>
          )}
        </td>
      </tr>
      <tr>
        <td colSpan={2} style={{ ...p }}>
          {props.item.MarketplaceOrderLines.map((o) => (
            <P2GGrid container alignItems="center" spacing={1} key={o.Id}>
              <P2GGrid item flexShrink={1}>
                <ProductImage product={o.Product} />
              </P2GGrid>
              <P2GGrid item flexGrow={1}>
                <P2GText component="p" variant="body2">
                  {o.Quantity} x{' '}
                  {o.Product.Sku && (
                    <P2GText colour="primary">
                      <strong>{o.Product.Sku} </strong>
                    </P2GText>
                  )}
                  {o.Product.Description}
                </P2GText>
                {o.Source && (
                  <P2GText component="p" variant="body2">
                    ({o.Source.Name}) {GetPickingListMarketPlaceOrderIds(props.item, o)}
                  </P2GText>
                )}
              </P2GGrid>
            </P2GGrid>
          ))}
        </td>
        <td align="right" style={{ ...p }}>
          {sourceSupportsMarkingAsDispatched && (
            <div>
              <P2GCheckBoxLegacy
                labelPlacement="start"
                variant="primary"
                error={false}
                id="Dispatched"
                label={t('DISPATCHED_INPUT_LABEL', { defaultValue: 'Dispatched' })}
                disabled={props.dispatched || props.working}
                checked={props.dispatched}
                onChange={() => props.action('MARK-AS-DISPATCHED', props.item.Id)}
              />
            </div>
          )}
          <div>
            <P2GCheckBoxLegacy
              labelPlacement="start"
              variant="primary"
              error={false}
              id="Packaged"
              label={t('PACKAGED_INPUT_LABEL', { defaultValue: 'Packaged' })}
              disabled={props.working}
              checked={props.packaged}
              onChange={() => props.action(props.packaged ? 'MARK-AS-UNPACKAGED' : 'MARK-AS-PACKAGED', props.item.Id)}
            />
          </div>
        </td>
      </tr>
    </Fragment>
  );
}
